import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, Phone, Send, Shirt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Seo } from "@/components/Seo";
import { FadeUp, SectionHeading } from "@/components/anim";
import { api } from "@/lib/api";
import { CONTACT, SITE_URL } from "@/lib/site";

const COSTUME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Children's Musical Theatre Costume Hire",
  serviceType: "Costume hire",
  provider: { "@type": "LocalBusiness", name: "Kerry's" },
  areaServed: "Whitstable, Kent",
  url: `${SITE_URL}/costume-hire`,
  description: "Musical theatre costume hire for children aged 2\u201318, carefully stored in Whitstable, Kent.",
};

const STEPS = [
  { n: "1", title: "Send your query", text: "Use the form above \u2014 the show, the song, the uniform or just the vibe, plus your child's age and rough size." },
  { n: "2", title: "We check the rails", text: "Kerry will look through the collection and send you what fits the bill (and the child)." },
  { n: "3", title: "Collect in Whitstable", text: "Try it, hire it, take your bow. Everything is stored carefully right here in town." },
];

const PLACEHOLDER_RAILS = [
  { title: "Show costumes", note: "Matilda, Annie, Oliver!, panto and more \u2014 photographed piece by piece." },
  { title: "Uniform & class wear", note: "Ballet, tap and modern uniform pieces in children's sizes." },
  { title: "Showstoppers", note: "The sparkly ones. Finale-worthy pieces for festivals and solos." },
];

const EMPTY_FORM = { name: "", email: "", phone: "", item: "", child_age: "", message: "" };

const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Please enter a valid email address");
    if (!form.item.trim()) return toast.error("Tell us which uniform or costume you're after");
    setSending(true);
    try {
      await api.post("/costume-enquiries", form);
      setSent(true);
      toast.success("Query sent \u2014 Kerry will get back to you soon!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong \u2014 please try again or email us");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun-pale))] grain p-8 sm:p-10 h-full flex flex-col justify-center" data-testid="costume-enquiry-success">
        <CheckCircle2 className="h-10 w-10 text-[hsl(var(--kerrys-sea))]" />
        <h3 className="font-display uppercase text-4xl leading-[0.95] mt-5">Got it &mdash; thank you!</h3>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Your query is on its way to Kerry. She&rsquo;ll check the rails and reply to <span className="font-semibold text-foreground">{form.email}</span> &mdash; usually within a day or two.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setForm(EMPTY_FORM);
            setSent(false);
          }}
          className="mt-8 w-fit rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
          data-testid="costume-enquiry-again-button"
        >
          Ask about another piece
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border-[0.5px] border-foreground bg-card p-6 sm:p-8 h-full" data-testid="costume-enquiry-form" noValidate>
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 bg-[hsl(var(--kerrys-coral))] border-[0.5px] border-foreground flex items-center justify-center">
          <Shirt className="h-4 w-4" />
        </span>
        <h2 className="font-display uppercase text-3xl leading-none">Ask about a piece</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Uniform or costume &mdash; tell us what you&rsquo;re after and Kerry will check the rails for you.
      </p>
      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="enq-name" className="text-[11px] font-bold uppercase tracking-[0.14em]">Your name *</Label>
            <Input id="enq-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Sam Taylor" className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enq-email" className="text-[11px] font-bold uppercase tracking-[0.14em]">Email *</Label>
            <Input id="enq-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@email.com" className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-email-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="enq-item" className="text-[11px] font-bold uppercase tracking-[0.14em]">Which uniform or costume? *</Label>
          <Input id="enq-item" value={form.item} onChange={(e) => setField("item", e.target.value)} placeholder={"e.g. Matilda school uniform \u00b7 Oliver waistcoat \u00b7 ballet uniform"} className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-item-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="enq-age" className="text-[11px] font-bold uppercase tracking-[0.14em]">Child's age <span className="normal-case font-medium text-muted-foreground">(optional)</span></Label>
            <Input id="enq-age" value={form.child_age} onChange={(e) => setField("child_age", e.target.value)} placeholder="e.g. 8" className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-age-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enq-phone" className="text-[11px] font-bold uppercase tracking-[0.14em]">Phone <span className="normal-case font-medium text-muted-foreground">(optional)</span></Label>
            <Input id="enq-phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder={"07\u2026"} className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-phone-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="enq-message" className="text-[11px] font-bold uppercase tracking-[0.14em]">Anything else? <span className="normal-case font-medium text-muted-foreground">(optional)</span></Label>
          <Textarea id="enq-message" rows={3} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder={"Dates you need it, sizes, the show it's for\u2026"} className="rounded-none border-[0.5px] border-foreground/60 focus-visible:ring-[hsl(var(--kerrys-sea))]" data-testid="costume-enquiry-message-input" />
        </div>
        <Button
          type="submit"
          disabled={sending}
          className="w-full rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm h-12"
          data-testid="costume-enquiry-submit-button"
        >
          {sending ? "Sending\u2026" : (<><Send className="h-4 w-4 mr-2" /> Send my query</>)}
        </Button>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Prefer to talk? Call <a href={CONTACT.phoneHref} className="font-semibold text-foreground underline underline-offset-2" data-testid="costume-enquiry-phone-link">{CONTACT.phone}</a> or email{" "}
          <a href={CONTACT.emailHref} className="font-semibold text-foreground underline underline-offset-2" data-testid="costume-enquiry-email-link">{CONTACT.email}</a>.
        </p>
      </div>
    </form>
  );
};

export default function CostumeHire() {
  return (
    <>
      <Seo
        title="Costume Hire"
        description={"Musical theatre costume and uniform hire for children aged 2\u201318 \u2014 a carefully stored collection in Whitstable, Kent. Send a quick query about any uniform or costume and Kerry will check the rails for you."}
        path="/costume-hire"
        jsonLd={COSTUME_JSONLD}
      />
      <section className="grain border-b-[0.5px] border-foreground/30 overflow-hidden" data-testid="costumes-hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <FadeUp>
              <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-card font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-1.5 mb-6">
                Ages 2&ndash;18 &middot; stored in Whitstable
              </Badge>
              <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6rem] leading-[0.88]">
                The costume
                <br />
                <span className="text-[hsl(var(--kerrys-coral))]">cupboard.</span>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                A carefully stored collection of musical theatre costumes and uniform pieces for children aged 2 to 18 &mdash;
                built up over years of shows and pantomimes, right here in Whitstable. Photos of every piece are on their
                way. Until then, the fastest route is simple: <span className="text-foreground font-semibold">ask us about the piece you need</span> and
                we&rsquo;ll check the rails for you.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="costumes-phone-button"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="h-4 w-4 mr-2" /> Call {CONTACT.phone}
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="costumes-email-button"
                >
                  <a href={CONTACT.emailHref}>
                    <Mail className="h-4 w-4 mr-2" /> Email instead
                  </a>
                </Button>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp delay={0.12}>
              <EnquiryForm />
            </FadeUp>
          </div>
        </div>
      </section>

      <div className="bg-[hsl(var(--kerrys-sun))] border-b-[0.5px] border-foreground/60 grain" data-testid="costumes-coming-soon-banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
          <p className="font-display uppercase text-xl sm:text-2xl leading-none">Photos &amp; piece profiles coming soon</p>
          <p className="text-sm text-foreground/70">We&rsquo;re photographing the collection piece by piece &mdash; queries are very welcome in the meantime.</p>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
        <SectionHeading
          eyebrow="What's on the rails"
          title="From ensemble to showstopper"
          sub={"Every piece will soon have its own photo and profile right here. Until then, here's what the rails hold \u2014 and the form above is the quickest way to ask about any of it."}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {PLACEHOLDER_RAILS.map((r, i) => (
            <FadeUp key={r.title} delay={i * 0.07} className={i === 1 ? "md:translate-y-8" : ""}>
              <div className="border-[0.5px] border-dashed border-foreground/60 bg-card p-8 h-full flex flex-col" data-testid="costumes-placeholder-tile">
                <div className="h-36 border-[0.5px] border-dashed border-foreground/40 bg-secondary/60 grain flex items-center justify-center">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-muted-foreground">Photos coming soon</p>
                </div>
                <h3 className="font-display uppercase text-3xl leading-[0.95] mt-6">{r.title}</h3>
                <p className="text-muted-foreground mt-3 leading-relaxed text-sm">{r.note}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 mt-14">
          <FadeUp className="sm:col-span-7">
            <div className="border-[0.5px] border-foreground bg-secondary grain p-8 sm:p-10 h-full" data-testid="costumes-info-tile">
              <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">Cared for properly</h3>
              <p className="text-muted-foreground mt-4 leading-relaxed max-w-prose">
                Every costume is cleaned, checked and carefully stored here in Whitstable between hires &mdash; so what
                arrives for your child looks the part, fits the part and is ready for the stage. No musty boxes, no missing
                sequins.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.08} className="sm:col-span-5 lg:translate-y-10">
            <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="costumes-info-tile">
              <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">Fair hire prices</h3>
              <p className="text-muted-foreground mt-4 leading-relaxed">
                Prices depend on the costume and how long you need it &mdash; tell us your dates and we&rsquo;ll give you a
                straightforward quote. No deposit drama.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-secondary border-y-[0.5px] border-foreground/40 grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
          <SectionHeading eyebrow="How it works" title="Hired in three steps" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.07} className={i === 1 ? "md:translate-y-8" : ""}>
                <div className="border-[0.5px] border-foreground bg-card p-8 h-full" data-testid="costumes-step">
                  <p className="font-display text-5xl text-[hsl(var(--kerrys-coral))]">{s.n}</p>
                  <h3 className="font-display uppercase text-3xl leading-[0.95] mt-3">{s.title}</h3>
                  <p className="text-muted-foreground mt-3 leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[hsl(var(--kerrys-ink))] text-background grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
          <FadeUp>
            <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">
              Got a show
              <br />
              coming up?
            </h2>
            <p className="mt-6 text-background/70 text-lg max-w-xl leading-relaxed">
              Get in early &mdash; popular costumes go quickly around show season.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="costumes-cta-enquire-button"
              >
                Send a costume query <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-none bg-transparent border-[0.5px] border-background/60 text-background hover:bg-background hover:text-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="costumes-cta-phone-button"
              >
                <a href={CONTACT.phoneHref}>
                  <Phone className="h-4 w-4 mr-2" /> {CONTACT.phone}
                </a>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
